import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { adminService } from '../services/admin.service';
import { Category, User, USER_TYPE_LABELS } from '../types';

interface State {
  id: string; name: string; uf: string; ibgeCode: string; region: string;
  boundary: any; isActive: boolean; createdAt: string; updatedAt: string;
}

interface City {
  id: string; name: string; state: string; ibgeCode: string;
  lat: number; lng: number; boundary: any; contratado: boolean; isActive: boolean;
  citizenRegistrationEnabled: boolean;
}

type Tab = 'states' | 'cities' | 'categories' | 'users';

export default function AdminPage() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const [tab, setTab] = useState<Tab>('states');
  const [states, setStates] = useState<State[]>([]);
  const [cities, setCities] = useState<City[]>([]);
  const [loading, setLoading] = useState(true);
  const [actionMsg, setActionMsg] = useState('');
  const [editingState, setEditingState] = useState<State | null>(null);
  const [editingCity, setEditingCity] = useState<City | null>(null);
  const [selectedStateId, setSelectedStateId] = useState('');

  const [categories, setCategories] = useState<Category[]>([]);
  const [editingCat, setEditingCat] = useState<Partial<Category> & { isNew?: boolean } | null>(null);

  const [users, setUsers] = useState<User[]>([]);
  const [editingUser, setEditingUser] = useState<Partial<User> & { isNew?: boolean; password?: string } | null>(null);

  const isGlobalAdmin = user?.groups?.some(g => g.name === 'admins') ?? false;
  const isCityAdmin = user?.userType === 'admin';

  useEffect(() => {
    if (!user || (!isGlobalAdmin && !isCityAdmin)) {
      navigate('/');
      return;
    }
    if (!isGlobalAdmin && tab === 'states') setTab('categories');
    loadAll();
  }, []);

  const loadAll = async () => {
    setLoading(true);
    try {
      const s = await adminService.getStates();
      setStates(s);
    } catch { }
    try {
      const c = await adminService.getCities();
      setCities(c);
    } catch { }
    try {
      const cats = await adminService.getCategories();
      setCategories(cats);
    } catch { }
    try {
      const u = await adminService.getUsers();
      setUsers(u);
    } catch { }
    setLoading(false);
  };

  const msg = (text: string) => { setActionMsg(text); setTimeout(() => setActionMsg(''), 4000); };

  const importStates = async () => {
    try {
      const result = await adminService.importStates();
      setStates(result);
      msg(`${result.length} estados importados do IBGE`);
    } catch (e: any) {
      msg(e?.response?.data?.message || 'Erro ao importar estados');
    }
  };

  const importCities = async (stateId: string) => {
    try {
      const result = await adminService.importCities(stateId);
      const all = await adminService.getCities();
      setCities(all);
      msg(`${result.length} municípios importados`);
    } catch (e: any) {
      msg(e?.response?.data?.message || 'Erro ao importar municípios');
    }
  };

  const fetchCityBoundary = async (cityId: string) => {
    try {
      await adminService.fetchCityBoundary(cityId);
      const all = await adminService.getCities();
      setCities(all);
      msg('Limite municipal salvo');
    } catch (e: any) {
      msg(e?.response?.data?.message || 'Erro ao buscar limite');
    }
  };

  const fetchStateBoundary = async (stateId: string) => {
    try {
      await adminService.fetchStateBoundary(stateId);
      msg('Limite estadual salvo');
    } catch (e: any) {
      msg(e?.response?.data?.message || 'Erro ao buscar limite estadual');
    }
  };

  const deleteCity = async (id: string) => {
    if (!confirm('Excluir esta cidade?')) return;
    try {
      await adminService.deleteCity(id);
      setCities((prev) => prev.filter((c) => c.id !== id));
      msg('Cidade excluída');
    } catch (e: any) {
      msg(e?.response?.data?.message || 'Erro ao excluir');
    }
  };

  const deleteState = async (id: string) => {
    if (!confirm('Excluir este estado?')) return;
    try {
      await adminService.deleteState(id);
      setStates((prev) => prev.filter((s) => s.id !== id));
      msg('Estado excluído');
    } catch (e: any) {
      msg(e?.response?.data?.message || 'Erro ao excluir');
    }
  };

  const saveCategory = async () => {
    if (!editingCat) return;
    try {
      if (editingCat.isNew) {
        const created = await adminService.createCategory({
          name: editingCat.name!,
          description: editingCat.description,
          icon: editingCat.icon,
          color: editingCat.color,
          pin: editingCat.pin || null,
          cityId: editingCat.cityId!,
        });
        setCategories((prev) => [...prev, created]);
      } else {
        const updated = await adminService.updateCategory(editingCat.id!, {
          name: editingCat.name,
          description: editingCat.description,
          icon: editingCat.icon,
          color: editingCat.color,
          pin: editingCat.pin || null,
          isActive: editingCat.isActive,
        });
        setCategories((prev) => prev.map((c) => (c.id === updated.id ? updated : c)));
      }
      setEditingCat(null);
      msg('Categoria salva');
    } catch (e: any) {
      msg(e?.response?.data?.message || 'Erro ao salvar categoria');
    }
  };

  const deleteCategory = async (id: string) => {
    if (!confirm('Excluir esta categoria?')) return;
    try {
      await adminService.deleteCategory(id);
      setCategories((prev) => prev.filter((c) => c.id !== id));
      msg('Categoria excluída');
    } catch (e: any) {
      msg(e?.response?.data?.message || 'Erro ao excluir');
    }
  };

  const saveUser = async () => {
    if (!editingUser) return;
    try {
      if (editingUser.isNew) {
        const created = await adminService.createUser({
          name: editingUser.name!,
          email: editingUser.email!,
          password: editingUser.password!,
          cityId: editingUser.cityId!,
          userType: editingUser.userType || 'citizen',
        });
        setUsers((prev) => [...prev, created]);
      } else {
        const payload: any = { name: editingUser.name, userType: editingUser.userType, isActive: editingUser.isActive };
        if (editingUser.email) payload.email = editingUser.email;
        if (editingUser.cityId) payload.cityId = editingUser.cityId;
        const updated = await adminService.updateUser(editingUser.id!, payload);
        setUsers((prev) => prev.map((u) => (u.id === updated.id ? updated : u)));
      }
      setEditingUser(null);
      msg('Usuário salvo');
    } catch (e: any) {
      msg(e?.response?.data?.message || e.message || 'Erro ao salvar usuário');
    }
  };

  const deleteUser = async (id: string) => {
    if (!confirm('Excluir este usuário?')) return;
    try {
      await adminService.deleteUser(id);
      setUsers((prev) => prev.filter((u) => u.id !== id));
      msg('Usuário excluído');
    } catch (e: any) {
      msg(e?.response?.data?.message || 'Erro ao excluir');
    }
  };

  const saveStateEdit = async () => {
    if (!editingState) return;
    try {
      const updated = await adminService.updateState(editingState.id, {
        name: editingState.name,
        uf: editingState.uf,
        ibgeCode: editingState.ibgeCode,
        region: editingState.region,
      });
      setStates((prev) => prev.map((s) => (s.id === updated.id ? updated : s)));
      setEditingState(null);
      msg('Estado atualizado');
    } catch (e: any) {
      msg(e?.response?.data?.message || 'Erro ao atualizar');
    }
  };

  const saveCityEdit = async () => {
    if (!editingCity) return;
    try {
      const updated = await adminService.updateCity(editingCity.id, {
        name: editingCity.name,
        lat: editingCity.lat,
        lng: editingCity.lng,
        slug: editingCity.slug || null,
        contratado: editingCity.contratado,
        citizenRegistrationEnabled: editingCity.citizenRegistrationEnabled,
      });
      setCities((prev) => prev.map((c) => (c.id === updated.id ? updated : c)));
      setEditingCity(null);
      msg('Cidade atualizada');
    } catch (e: any) {
      msg(e?.response?.data?.message || 'Erro ao atualizar');
    }
  };

  const filteredCities = selectedStateId
    ? cities.filter((c) => {
        const st = states.find((s) => s.id === selectedStateId);
        return c.state === st?.uf;
      })
    : cities;

  if (!user) return null;

  return (
    <>
        {actionMsg && (
          <div className="mb-4 bg-blue-50 border border-blue-200 text-blue-700 px-4 py-3 rounded-md text-sm">
            {actionMsg}
          </div>
        )}

        <div className="flex gap-4 mb-6 border-b">
          {isGlobalAdmin && (
            <button onClick={() => setTab('states')}
              className={`pb-2 px-1 text-sm font-medium border-b-2 transition-colors ${
                tab === 'states' ? 'border-primary-500 text-primary-600' : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}>Estados</button>
          )}
          {isGlobalAdmin && (
            <button onClick={() => setTab('cities')}
              className={`pb-2 px-1 text-sm font-medium border-b-2 transition-colors ${
                tab === 'cities' ? 'border-primary-500 text-primary-600' : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}>Municípios</button>
          )}
          <button onClick={() => setTab('categories')}
            className={`pb-2 px-1 text-sm font-medium border-b-2 transition-colors ${
              tab === 'categories' ? 'border-primary-500 text-primary-600' : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}>Categorias</button>
          <button onClick={() => setTab('users')}
            className={`pb-2 px-1 text-sm font-medium border-b-2 transition-colors ${
              tab === 'users' ? 'border-primary-500 text-primary-600' : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}>Usuários</button>
        </div>

        {loading ? (
          <p className="text-gray-500">Carregando...</p>
        ) : tab === 'states' ? (
          <StatesPanel
            states={states}
            onImport={importStates}
            onFetchBoundary={fetchStateBoundary}
            onDelete={deleteState}
            onEdit={(s) => setEditingState(s)}
          />
        ) : tab === 'users' ? (
          <UsersPanel
            users={users}
            cities={cities}
            onNew={() => setEditingUser({ isNew: true, name: '', email: '', password: '', cityId: user?.cityId || '', userType: 'citizen', isActive: true })}
            onEdit={(u) => setEditingUser({ ...u })}
            onDelete={deleteUser}
          />
        ) : tab === 'categories' ? (
          <CategoriesPanel
            categories={categories}
            cities={cities}
            onNew={() => setEditingCat({ isNew: true, isActive: true, name: '', cityId: user?.cityId || '' })}
            onEdit={(c) => setEditingCat({ ...c })}
            onDelete={deleteCategory}
          />
        ) : (
          <CitiesPanel
            states={states}
            cities={filteredCities}
            selectedStateId={selectedStateId}
            onSelectState={setSelectedStateId}
            onImport={importCities}
            onFetchBoundary={fetchCityBoundary}
            onDelete={deleteCity}
            onEdit={(c) => setEditingCity(c)}
          />
        )}

        {editingState && (
          <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50" onClick={() => setEditingState(null)}>
            <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md mx-4" onClick={(e) => e.stopPropagation()}>
              <h3 className="text-lg font-semibold mb-4">Editar Estado</h3>
              <div className="space-y-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Nome</label>
                  <input type="text" value={editingState.name} onChange={(e) => setEditingState({ ...editingState, name: e.target.value })}
                    className="block w-full px-3 py-2 border border-gray-300 rounded-md" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">UF</label>
                  <input type="text" value={editingState.uf} onChange={(e) => setEditingState({ ...editingState, uf: e.target.value.toUpperCase().slice(0, 2) })}
                    className="block w-full px-3 py-2 border border-gray-300 rounded-md" maxLength={2} />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Código IBGE</label>
                  <input type="text" value={editingState.ibgeCode} onChange={(e) => setEditingState({ ...editingState, ibgeCode: e.target.value })}
                    className="block w-full px-3 py-2 border border-gray-300 rounded-md" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Região</label>
                  <input type="text" value={editingState.region} onChange={(e) => setEditingState({ ...editingState, region: e.target.value })}
                    className="block w-full px-3 py-2 border border-gray-300 rounded-md" />
                </div>
              </div>
              <div className="flex justify-end gap-3 mt-6">
                <button onClick={() => setEditingState(null)} className="btn-secondary text-sm">Cancelar</button>
                <button onClick={saveStateEdit} className="btn-primary text-sm">Salvar</button>
              </div>
            </div>
          </div>
        )}

        {editingCity && (
          <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50" onClick={() => setEditingCity(null)}>
            <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md mx-4" onClick={(e) => e.stopPropagation()}>
              <h3 className="text-lg font-semibold mb-4">Editar Município</h3>
              <div className="space-y-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Nome</label>
                  <input type="text" value={editingCity.name} onChange={(e) => setEditingCity({ ...editingCity, name: e.target.value })}
                    className="block w-full px-3 py-2 border border-gray-300 rounded-md" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Latitude</label>
                  <input type="number" step="any" value={editingCity.lat} onChange={(e) => setEditingCity({ ...editingCity, lat: +e.target.value })}
                    className="block w-full px-3 py-2 border border-gray-300 rounded-md" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Longitude</label>
                  <input type="number" step="any" value={editingCity.lng} onChange={(e) => setEditingCity({ ...editingCity, lng: +e.target.value })}
                    className="block w-full px-3 py-2 border border-gray-300 rounded-md" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Slug do domínio</label>
                  <input type="text" value={editingCity.slug || ''} onChange={(e) => setEditingCity({ ...editingCity, slug: e.target.value })}
                    className="block w-full px-3 py-2 border border-gray-300 rounded-md" placeholder="ex: blumenau" />
                </div>
                <div className="flex items-center gap-2">
                  <input type="checkbox" id="city-contratado" checked={editingCity.contratado ?? false}
                    onChange={(e) => setEditingCity({ ...editingCity, contratado: e.target.checked })}
                    className="rounded border-gray-300" />
                  <label htmlFor="city-contratado" className="text-sm text-gray-700">Contratado (sistema liberado)</label>
                </div>
                <div className="flex items-center gap-2">
                  <input type="checkbox" id="city-registration" checked={editingCity.citizenRegistrationEnabled ?? true}
                    onChange={(e) => setEditingCity({ ...editingCity, citizenRegistrationEnabled: e.target.checked })}
                    className="rounded border-gray-300" />
                  <label htmlFor="city-registration" className="text-sm text-gray-700">Cadastro de cidadão habilitado</label>
                </div>
              </div>
              <div className="flex justify-end gap-3 mt-6">
                <button onClick={() => setEditingCity(null)} className="btn-secondary text-sm">Cancelar</button>
                <button onClick={saveCityEdit} className="btn-primary text-sm">Salvar</button>
              </div>
            </div>
          </div>
        )}

        {editingUser && (
          <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50" onClick={() => setEditingUser(null)}>
            <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md mx-4" onClick={(e) => e.stopPropagation()}>
              <h3 className="text-lg font-semibold mb-4">{editingUser.isNew ? 'Novo Usuário' : 'Editar Usuário'}</h3>
              <div className="space-y-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Nome</label>
                  <input type="text" value={editingUser.name || ''} onChange={(e) => setEditingUser({ ...editingUser, name: e.target.value })}
                    className="block w-full px-3 py-2 border border-gray-300 rounded-md" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Email</label>
                  <input type="email" value={editingUser.email || ''} onChange={(e) => setEditingUser({ ...editingUser, email: e.target.value })}
                    className="block w-full px-3 py-2 border border-gray-300 rounded-md" />
                </div>
                {editingUser.isNew && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Senha</label>
                    <input type="password" value={editingUser.password || ''} onChange={(e) => setEditingUser({ ...editingUser, password: e.target.value })}
                      className="block w-full px-3 py-2 border border-gray-300 rounded-md" />
                  </div>
                )}
                <div>
                  <label className="block text-sm font-medium text-gray-700">Tipo</label>
                  <select value={editingUser.userType || 'citizen'} onChange={(e) => setEditingUser({ ...editingUser, userType: e.target.value as any })}
                    className="block w-full px-3 py-2 border border-gray-300 rounded-md">
                    {(Object.entries(USER_TYPE_LABELS) as [string, string][]).filter(([key]) => {
                      if (key !== 'citizen') return true;
                      const selectedCity = cities.find((c) => c.id === editingUser.cityId);
                      return selectedCity ? selectedCity.citizenRegistrationEnabled : true;
                    }).map(([key, label]) => (
                      <option key={key} value={key}>{label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Município</label>
                  {!isGlobalAdmin ? (
                    <p className="text-sm text-gray-700 py-2 px-1">{cities.find((c) => c.id === editingUser.cityId)?.name || editingUser.cityId}</p>
                  ) : (
                    <select value={editingUser.cityId || ''} onChange={(e) => setEditingUser({ ...editingUser, cityId: e.target.value })}
                      className="block w-full px-3 py-2 border border-gray-300 rounded-md">
                      <option value="">Selecione</option>
                      {cities.filter((c) => c.contratado).map((c) => <option key={c.id} value={c.id}>{c.name} ({c.state})</option>)}
                    </select>
                  )}
                </div>
                {!editingUser.isNew && (
                  <div className="flex items-center gap-2">
                    <input type="checkbox" id="user-active" checked={editingUser.isActive ?? true}
                      onChange={(e) => setEditingUser({ ...editingUser, isActive: e.target.checked })}
                      className="rounded border-gray-300" />
                    <label htmlFor="user-active" className="text-sm text-gray-700">Ativo</label>
                  </div>
                )}
              </div>
              <div className="flex justify-end gap-3 mt-6">
                <button onClick={() => setEditingUser(null)} className="btn-secondary text-sm">Cancelar</button>
                <button onClick={saveUser} className="btn-primary text-sm">Salvar</button>
              </div>
            </div>
          </div>
        )}

        {editingCat && (
          <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50" onClick={() => setEditingCat(null)}>
            <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md mx-4" onClick={(e) => e.stopPropagation()}>
              <h3 className="text-lg font-semibold mb-4">{editingCat.isNew ? 'Nova Categoria' : 'Editar Categoria'}</h3>
              <div className="space-y-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Nome</label>
                  <input type="text" value={editingCat.name || ''} onChange={(e) => setEditingCat({ ...editingCat, name: e.target.value })}
                    className="block w-full px-3 py-2 border border-gray-300 rounded-md" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Descrição</label>
                  <input type="text" value={editingCat.description || ''} onChange={(e) => setEditingCat({ ...editingCat, description: e.target.value })}
                    className="block w-full px-3 py-2 border border-gray-300 rounded-md" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Ícone</label>
                  <input type="text" value={editingCat.icon || ''} onChange={(e) => setEditingCat({ ...editingCat, icon: e.target.value })}
                    className="block w-full px-3 py-2 border border-gray-300 rounded-md" placeholder="Ex: buraco" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Cor</label>
                  <input type="color" value={editingCat.color || '#6b7280'} onChange={(e) => setEditingCat({ ...editingCat, color: e.target.value })}
                    className="block w-full h-10 px-1 py-1 border border-gray-300 rounded-md" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Pin SVG</label>
                  <textarea value={editingCat.pin || ''} onChange={(e) => setEditingCat({ ...editingCat, pin: e.target.value })}
                    rows={4}
                    className="block w-full px-3 py-2 border border-gray-300 rounded-md font-mono text-xs"
                    placeholder='&lt;svg viewBox="0 0 32 32" width="32" height="32"&gt;...&lt;/svg&gt;' />
                  {editingCat.pin && (
                    <div className="mt-2 flex items-center gap-3">
                      <div dangerouslySetInnerHTML={{ __html: editingCat.pin }} />
                      <span className="text-xs text-gray-400">Pré-visualização</span>
                    </div>
                  )}
                </div>
                {editingCat.isNew && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Município</label>
                    {!isGlobalAdmin ? (
                      <p className="text-sm text-gray-700 py-2 px-1">{cities.find((c) => c.id === editingCat.cityId)?.name || editingCat.cityId}</p>
                    ) : (
                      <select value={editingCat.cityId || ''} onChange={(e) => setEditingCat({ ...editingCat, cityId: e.target.value })}
                        className="block w-full px-3 py-2 border border-gray-300 rounded-md">
                        <option value="">Selecione</option>
                        {cities.map((c) => <option key={c.id} value={c.id}>{c.name} ({c.state})</option>)}
                      </select>
                    )}
                  </div>
                )}
                {!editingCat.isNew && (
                  <div className="flex items-center gap-2">
                    <input type="checkbox" id="cat-active" checked={editingCat.isActive ?? true}
                      onChange={(e) => setEditingCat({ ...editingCat, isActive: e.target.checked })}
                      className="rounded border-gray-300" />
                    <label htmlFor="cat-active" className="text-sm text-gray-700">Ativo</label>
                  </div>
                )}
              </div>
              <div className="flex justify-end gap-3 mt-6">
                <button onClick={() => setEditingCat(null)} className="btn-secondary text-sm">Cancelar</button>
                <button onClick={saveCategory} className="btn-primary text-sm">Salvar</button>
              </div>
            </div>
          </div>
        )}
    </>
  );
}

function StatesPanel({
  states, onImport, onFetchBoundary, onDelete, onEdit,
}: {
  states: State[];
  onImport: () => void;
  onFetchBoundary: (id: string) => void;
  onDelete: (id: string) => void;
  onEdit: (s: State) => void;
}) {
  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <p className="text-sm text-gray-500">{states.length} estados cadastrados</p>
        <button onClick={onImport} className="btn-primary text-sm">Importar Estados do IBGE</button>
      </div>
      <div className="bg-white rounded-lg shadow overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 border-b">
            <tr>
              <th className="text-left px-4 py-3 font-medium text-gray-600">Nome</th>
              <th className="text-left px-4 py-3 font-medium text-gray-600">UF</th>
              <th className="text-left px-4 py-3 font-medium text-gray-600">Região</th>
              <th className="text-left px-4 py-3 font-medium text-gray-600">IBGE</th>
              <th className="text-center px-4 py-3 font-medium text-gray-600">Limite</th>
              <th className="text-right px-4 py-3 font-medium text-gray-600">Ações</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {states.map((s) => (
              <tr key={s.id} className="hover:bg-gray-50">
                <td className="px-4 py-3">{s.name}</td>
                <td className="px-4 py-3 font-medium">{s.uf}</td>
                <td className="px-4 py-3 text-gray-500">{s.region}</td>
                <td className="px-4 py-3 text-gray-500">{s.ibgeCode}</td>
                <td className="px-4 py-3 text-center">
                  {s.boundary ? (
                    <span className="status-resolved">OK</span>
                  ) : (
                    <span className="text-gray-400">—</span>
                  )}
                </td>
                <td className="px-4 py-3 text-right">
                  <div className="flex justify-end gap-2">
                    <button onClick={() => onFetchBoundary(s.id)} className="text-xs text-primary-600 hover:text-primary-700" title="Buscar limite">
                      Limite
                    </button>
                    <button onClick={() => onEdit(s)} className="text-xs text-blue-600 hover:text-blue-700">Editar</button>
                    <button onClick={() => onDelete(s.id)} className="text-xs text-red-600 hover:text-red-700">Excluir</button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function CategoriesPanel({
  categories, cities, onNew, onEdit, onDelete,
}: {
  categories: Category[];
  cities: City[];
  onNew: () => void;
  onEdit: (c: Category) => void;
  onDelete: (id: string) => void;
}) {
  const [filterCity, setFilterCity] = useState('');
  const contratadoCities = cities.filter((c) => c.contratado);
  const filtered = filterCity ? categories.filter((cat) => cat.cityId === filterCity) : categories;
  return (
    <div>
      <div className="flex flex-wrap gap-3 items-center mb-4">
        <select value={filterCity} onChange={(e) => setFilterCity(e.target.value)}
          className="px-3 py-2 border border-gray-300 rounded-md text-sm">
          <option value="">Todos os municípios</option>
          {contratadoCities.map((c) => <option key={c.id} value={c.id}>{c.name} ({c.state})</option>)}
        </select>
        <button onClick={onNew} className="btn-primary text-sm">Nova Categoria</button>
        <p className="text-sm text-gray-500">{filtered.length} categorias</p>
      </div>
      <div className="bg-white rounded-lg shadow overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 border-b">
            <tr>
              <th className="text-left px-4 py-3 font-medium text-gray-600">Nome</th>
              <th className="text-left px-4 py-3 font-medium text-gray-600">Ícone</th>
              <th className="text-left px-4 py-3 font-medium text-gray-600">Cor</th>
              <th className="text-center px-4 py-3 font-medium text-gray-600">Pin</th>
              <th className="text-left px-4 py-3 font-medium text-gray-600">Município</th>
              <th className="text-center px-4 py-3 font-medium text-gray-600">Ativo</th>
              <th className="text-right px-4 py-3 font-medium text-gray-600">Ações</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {filtered.map((cat) => {
              const city = cities.find((c) => c.id === cat.cityId);
              return (
                <tr key={cat.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3">
                    <span className="flex items-center gap-2">
                      <span className="w-3 h-3 rounded-full inline-block" style={{ backgroundColor: cat.color || '#6b7280' }}></span>
                      {cat.name}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-gray-500">{cat.icon || '—'}</td>
                  <td className="px-4 py-3 text-gray-500">{cat.color || '—'}</td>
                  <td className="px-4 py-3 text-center">{cat.pin ? <span className="text-xs text-green-600" title="SVG definido">✓</span> : <span className="text-gray-300">—</span>}</td>
                  <td className="px-4 py-3 text-gray-500">{city ? `${city.name} (${city.state})` : cat.cityId}</td>
                  <td className="px-4 py-3 text-center">
                    {cat.isActive ? <span className="status-resolved">Sim</span> : <span className="text-gray-400">Não</span>}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex justify-end gap-2">
                      <button onClick={() => onEdit(cat)} className="text-xs text-blue-600 hover:text-blue-700">Editar</button>
                      <button onClick={() => onDelete(cat.id)} className="text-xs text-red-600 hover:text-red-700">Excluir</button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function formatCpf(cpf?: string): string {
  if (!cpf || cpf.length !== 11) return cpf || '';
  return cpf.replace(/^(\d{3})(\d{3})(\d{3})(\d{2})$/, '$1.$2.$3-$4');
}

function formatPhone(phone?: string): string {
  if (!phone) return '';
  const d = phone.replace(/\D/g, '');
  if (d.length === 11) return d.replace(/^(\d{2})(\d{5})(\d{4})$/, '($1) $2-$3');
  if (d.length === 10) return d.replace(/^(\d{2})(\d{4})(\d{4})$/, '($1) $2-$3');
  return phone;
}

function UsersPanel({
  users, cities, onNew, onEdit, onDelete,
}: {
  users: User[];
  cities: City[];
  onNew: () => void;
  onEdit: (u: User) => void;
  onDelete: (id: string) => void;
}) {
  const [filterCity, setFilterCity] = useState('');
  const contratadoCities = cities.filter((c) => c.contratado);
  const filtered = filterCity ? users.filter((u) => u.cityId === filterCity) : users;
  return (
    <div>
      <div className="flex flex-wrap gap-3 items-center mb-4">
        <select value={filterCity} onChange={(e) => setFilterCity(e.target.value)}
          className="px-3 py-2 border border-gray-300 rounded-md text-sm">
          <option value="">Todos os municípios</option>
          {contratadoCities.map((c) => <option key={c.id} value={c.id}>{c.name} ({c.state})</option>)}
        </select>
        <button onClick={onNew} className="btn-primary text-sm">Novo Usuário</button>
        <p className="text-sm text-gray-500">{filtered.length} usuários</p>
      </div>
      <div className="bg-white rounded-lg shadow overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 border-b">
            <tr>
              <th className="text-left px-4 py-3 font-medium text-gray-600">Nome</th>
              <th className="text-left px-4 py-3 font-medium text-gray-600">Email</th>
              <th className="text-left px-4 py-3 font-medium text-gray-600">CPF</th>
              <th className="text-left px-4 py-3 font-medium text-gray-600">Telefone</th>
              <th className="text-center px-4 py-3 font-medium text-gray-600">WhatsApp</th>
              <th className="text-left px-4 py-3 font-medium text-gray-600">Tipo</th>
              <th className="text-left px-4 py-3 font-medium text-gray-600">Município</th>
              <th className="text-center px-4 py-3 font-medium text-gray-600">Ativo</th>
              <th className="text-center px-4 py-3 font-medium text-gray-600">Verificado</th>
              <th className="text-left px-4 py-3 font-medium text-gray-600">Cadastro</th>
              <th className="text-right px-4 py-3 font-medium text-gray-600">Ações</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {filtered.map((u) => {
              const city = cities.find((c) => c.id === u.cityId);
              return (
                <tr key={u.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3">{u.name}</td>
                  <td className="px-4 py-3 text-gray-500">{u.email}</td>
                  <td className="px-4 py-3 text-gray-500">{formatCpf(u.cpf)}</td>
                  <td className="px-4 py-3 text-gray-500">{formatPhone(u.phone)}</td>
                  <td className="px-4 py-3 text-center">
                    {u.whatsapp ? <span className="status-resolved">Sim</span> : <span className="text-gray-400">Não</span>}
                  </td>
                  <td className="px-4 py-3">{USER_TYPE_LABELS[u.userType as keyof typeof USER_TYPE_LABELS] || u.userType}</td>
                  <td className="px-4 py-3 text-gray-500">{city ? `${city.name} (${city.state})` : u.cityId}</td>
                  <td className="px-4 py-3 text-center">
                    {u.isActive ? <span className="status-resolved">Sim</span> : <span className="text-gray-400">Não</span>}
                  </td>
                  <td className="px-4 py-3 text-center">
                    {u.verified ? <span className="status-resolved">Sim</span> : <span className="text-gray-400">Não</span>}
                  </td>
                  <td className="px-4 py-3 text-gray-500 text-sm">
                    {new Date(u.createdAt).toLocaleDateString('pt-BR')}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex justify-end gap-2">
                      <button onClick={() => onEdit(u)} className="text-xs text-blue-600 hover:text-blue-700">Editar</button>
                      <button onClick={() => onDelete(u.id)} className="text-xs text-red-600 hover:text-red-700">Excluir</button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function CitiesPanel({
  states, cities, selectedStateId, onSelectState, onImport, onFetchBoundary, onDelete, onEdit,
}: {
  states: State[];
  cities: City[];
  selectedStateId: string;
  onSelectState: (id: string) => void;
  onImport: (stateId: string) => void;
  onFetchBoundary: (id: string) => void;
  onDelete: (id: string) => void;
  onEdit: (c: City) => void;
}) {
  const [onlyContratado, setOnlyContratado] = useState(false);
  const filtered = onlyContratado ? cities.filter((c) => c.contratado) : cities;
  return (
    <div>
      <div className="flex flex-wrap gap-3 items-center mb-4">
        <select
          value={selectedStateId}
          onChange={(e) => onSelectState(e.target.value)}
          className="px-3 py-2 border border-gray-300 rounded-md text-sm"
        >
          <option value="">Todos os estados</option>
          {states.map((s) => (
            <option key={s.id} value={s.id}>{s.name} ({s.uf})</option>
          ))}
        </select>
        {selectedStateId && (
          <button onClick={() => onImport(selectedStateId)} className="btn-primary text-sm">
            Importar Municípios do IBGE
          </button>
        )}
        <label className="flex items-center gap-1.5 text-sm text-gray-600 cursor-pointer">
          <input type="checkbox" checked={onlyContratado} onChange={(e) => setOnlyContratado(e.target.checked)}
            className="rounded border-gray-300" />
          Apenas contratados
        </label>
        <p className="text-sm text-gray-500">{filtered.length} municípios</p>
      </div>
      <div className="bg-white rounded-lg shadow overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 border-b">
            <tr>
              <th className="text-left px-4 py-3 font-medium text-gray-600">Nome</th>
              <th className="text-left px-4 py-3 font-medium text-gray-600">UF</th>
              <th className="text-left px-4 py-3 font-medium text-gray-600">IBGE</th>
              <th className="text-left px-4 py-3 font-medium text-gray-600">Lat</th>
              <th className="text-left px-4 py-3 font-medium text-gray-600">Lng</th>
              <th className="text-center px-4 py-3 font-medium text-gray-600">Contratado</th>
              <th className="text-center px-4 py-3 font-medium text-gray-600">Limite</th>
              <th className="text-right px-4 py-3 font-medium text-gray-600">Ações</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {filtered.map((c) => (
              <tr key={c.id} className="hover:bg-gray-50">
                <td className="px-4 py-3">{c.name}</td>
                <td className="px-4 py-3 font-medium">{c.state}</td>
                <td className="px-4 py-3 text-gray-500">{c.ibgeCode}</td>
                <td className="px-4 py-3 text-gray-500">{c.lat}</td>
                <td className="px-4 py-3 text-gray-500">{c.lng}</td>
                <td className="px-4 py-3 text-center">
                  {c.contratado ? <span className="status-resolved">Sim</span> : <span className="text-gray-400">Não</span>}
                </td>
                <td className="px-4 py-3 text-center">
                  {c.boundary ? (
                    <span className="status-resolved">OK</span>
                  ) : (
                    <span className="text-gray-400">—</span>
                  )}
                </td>
                <td className="px-4 py-3 text-right">
                  <div className="flex justify-end gap-2">
                    <button onClick={() => onFetchBoundary(c.id)} className="text-xs text-primary-600 hover:text-primary-700" title="Buscar limite">
                      Limite
                    </button>
                    <button onClick={() => onEdit(c)} className="text-xs text-blue-600 hover:text-blue-700">Editar</button>
                    <button onClick={() => onDelete(c.id)} className="text-xs text-red-600 hover:text-red-700">Excluir</button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
